import * as Sentry from "@sentry/nextjs"
import { getDRSConstants } from "@/governance/constants/drs"
import { CuriaDRSProvider } from "@/governance/helpers/drs/providers/CuriaDRSProvider"
import { KarmaDRSProvider } from "@/governance/helpers/drs/providers/KarmaDRSProvider"
import {
  type DRSConfig,
  type DRSInfo,
  type DRSProvider,
  DRSProviderType,
} from "@/governance/types/drs"
import { EmptyProvider } from "./providers/EmptyProvider"

/**
 * Service for managing Delegate Reputation Score (DRS) providers
 * Supports multiple provider types (Karma, Curia) and handles their initialization
 */
export class DelegateReputationScoreService {
  public provider: DRSProvider
  public orgId: string
  public config: DRSConfig | null
  public isActive: boolean

  /**
   * Creates a new DRS service instance
   * @param orgId - The organization ID to get DRS configuration for
   */
  constructor(orgId: string) {
    const config = getDRSConstants(orgId)

    this.orgId = orgId
    this.config = config
    this.isActive = !!config
    this.provider = this.createProvider(config)
  }

  /**
   * Creates the appropriate provider based on configuration
   * @param config - DRS configuration or null
   * @returns The created provider instance
   * @throws Error if provider type is unsupported
   */
  private createProvider(config: DRSConfig | null): DRSProvider {
    if (!config) return new EmptyProvider()
    switch (config?.provider) {
      case DRSProviderType.KARMA:
        return new KarmaDRSProvider(config)
      case DRSProviderType.CURIA:
        return new CuriaDRSProvider(config)
      default:
        const error = new Error(`Unsupported provider: ${config?.provider}`)
        Sentry.captureException(error, {
          level: "error",
          tags: {
            service: "DelegateReputationScoreService",
            method: "createProvider",
          },
          extra: {
            provider: config?.provider,
            orgId: this.orgId,
          },
        })
        throw error
    }
  }

  /**
   * Initializes the DRS provider
   * @returns The initialized service instance
   * @throws Error if provider initialization fails
   */
  async initialize(): Promise<DelegateReputationScoreService> {
    try {
      await this.provider.initialize()
      return this
    } catch (error) {
      console.error("Failed to initialize DRS provider:", error)
      Sentry.captureException(error, {
        level: "error",
        tags: {
          service: "DelegateReputationScoreService",
          method: "initialize",
        },
        extra: {
          orgId: this.orgId,
          provider: this.config?.provider,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      })
      throw error
    }
  }

  /**
   * Gets DRS information for a single address
   * @param address - Ethereum address to query
   * @returns DRS info or null if error occurs
   */
  async getDRSInfo(address: string): Promise<DRSInfo | null> {
    try {
      return await this.provider.getDRSInfo(address)
    } catch (error) {
      console.error(`Failed to get DRS info for ${address}:`, error)
      return null
    }
  }

  /**
   * Gets DRS information for multiple addresses
   * @param addresses - Array of Ethereum addresses to query
   * @returns Map of address (lowercase) to DRS info
   */
  async getBulkDRSInfo(addresses: string[]): Promise<Map<string, DRSInfo>> {
    try {
      return await this.provider.getBulkDRSInfo(addresses)
    } catch (error) {
      console.error(`Failed to get bulk DRS info:`, error)
      return new Map()
    }
  }
}
